import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, User as UserIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { AuthLayout, FormError } from '@/components/auth/AuthLayout';
import HumanCheck, { HumanCheckRef, needsHumanCheck } from '@/components/auth/HumanCheck';
import PasswordField from '@/components/auth/PasswordField';
import { PASSWORD_HINT, strongPassword } from '@/components/auth/password';
import DateOfBirthField from '@/components/auth/DateOfBirthField';
import { ADULT_AGE, ageOn, isoDate, realDate } from '@/lib/age';

const isUnder18 = (d: { dobDay?: string; dobMonth?: string; dobYear?: string }) => {
  const [day, month, year] = [Number(d.dobDay), Number(d.dobMonth), Number(d.dobYear)];
  return !!(day && month && year) && realDate(year, month, day) && ageOn(year, month, day) < ADULT_AGE;
};

const registerSchema = z
  .object({
    name: z.string().min(2, 'Enter your name'),
    email: z.string().email('Enter a valid email address'),
    password: strongPassword,
    confirmPassword: z.string(),
    agreeToTerms: z.boolean().refine((v) => v === true, { message: 'Accept the Terms and Privacy Policy to continue' }),
    dobDay: z.string().optional(),
    dobMonth: z.string().optional(),
    dobYear: z.string().optional(),
    guardianEmail: z.string().optional(),
  })
  .refine((d) => d.password === d.confirmPassword, { message: "The passwords don't match", path: ['confirmPassword'] })
  // The gateway works out the age again and decides; these only catch mistakes early.
  .superRefine((d, ctx) => {
    if (!d.dobDay || !d.dobMonth || !d.dobYear) {
      ctx.addIssue({ code: 'custom', path: ['dobDay'], message: 'Enter your date of birth' });
      return;
    }
    if (!realDate(Number(d.dobYear), Number(d.dobMonth), Number(d.dobDay))) {
      ctx.addIssue({ code: 'custom', path: ['dobDay'], message: "That date doesn't exist. Check the day and month" });
    } else if (ageOn(Number(d.dobYear), Number(d.dobMonth), Number(d.dobDay)) < 0) {
      ctx.addIssue({ code: 'custom', path: ['dobDay'], message: "Your date of birth can't be in the future" });
    } else if (isUnder18(d)) {
      const g = d.guardianEmail?.trim() ?? '';
      if (!g) ctx.addIssue({ code: 'custom', path: ['guardianEmail'], message: "Enter your parent's or guardian's email address" });
      else if (!z.string().email().safeParse(g).success) ctx.addIssue({ code: 'custom', path: ['guardianEmail'], message: 'Enter a valid email address' });
      else if (g.toLowerCase() === d.email.trim().toLowerCase()) ctx.addIssue({ code: 'custom', path: ['guardianEmail'], message: 'Use a different email address from yours' });
    }
  });

type RegisterFormData = z.infer<typeof registerSchema>;


const RegisterPage: React.FC = () => {
  const { register: registerUser, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  // The button stays off until the invisible browser check hands back a token.
  const waitingForCheck = needsHumanCheck && !token;
  const [waitingOn, setWaitingOn] = useState<string | null>(null);
  const check = useRef<HumanCheckRef>(null);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<RegisterFormData>({ resolver: zodResolver(registerSchema) });
  const minor = isUnder18(watch());

  const onSubmit = async (data: RegisterFormData) => {
    setError(null);
    if (waitingForCheck) {
      setError('The browser check is still running. Try again in a moment.');
      return;
    }
    try {
      const result = await registerUser({
        email: data.email,
        password: data.password,
        fullName: data.name,
        turnstileToken: token ?? '',
        dateOfBirth: isoDate(Number(data.dobYear), Number(data.dobMonth), Number(data.dobDay)),
        guardianEmail: isUnder18(data) ? data.guardianEmail?.trim() : undefined,
      });
      // An adult is signed in (navigation handled by the useEffect watching
      // isAuthenticated); an under-18 waits for their guardian.
      if (result.guardianEmailMasked) setWaitingOn(result.guardianEmailMasked);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Try again.');
      check.current?.reset();
    }
  };

  if (waitingOn) {
    return (
      <AuthLayout title="Ask your parent or guardian to check their email">
        <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
          <p>We emailed them at <strong className="text-foreground">{waitingOn}</strong> with a link to give permission for your account.</p>
          <p>You can sign in once they say yes. If they don't answer within 7 days, we delete the account and you can sign up again.</p>
          <Link to="/login" className="inline-block font-medium text-accent-text hover:underline">Go to sign in</Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Create your account" subtitle="Free every month up to the allowance on each product, then pay as you go.">
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Input label="Full name" type="text" autoComplete="name" leftIcon={<UserIcon className="h-4 w-4" />} error={errors.name?.message} {...register('name')} />
        <Input label="Email address" type="email" autoComplete="email" leftIcon={<Mail className="h-4 w-4" />} error={errors.email?.message} {...register('email')} />
        <PasswordField label="Password" autoComplete="new-password" error={errors.password?.message} helperText={PASSWORD_HINT} {...register('password')} />
        <PasswordField label="Confirm password" autoComplete="new-password" error={errors.confirmPassword?.message} {...register('confirmPassword')} />
        <DateOfBirthField day={register('dobDay')} month={register('dobMonth')} year={register('dobYear')} error={errors.dobDay?.message} />
        {minor && (
          <div className="space-y-2">
            <Input label="Parent's or guardian's email" type="email" autoComplete="off" leftIcon={<Mail className="h-4 w-4" />}
              error={errors.guardianEmail?.message} {...register('guardianEmail')} />
            <p className="text-sm text-muted-foreground">Because you're under 18, a parent or guardian needs to give permission. We'll email them a link, and you can sign in once they say yes.</p>
          </div>
        )}
        <div>
          <label className="flex items-start gap-2.5 text-sm text-foreground">
            <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-border accent-[rgb(var(--color-accent))]" {...register('agreeToTerms')} />
            <span>
              I agree to the <Link to="/terms" className="text-accent-text hover:underline">Terms of Service</Link> and{' '}
              <Link to="/privacy" className="text-accent-text hover:underline">Privacy Policy</Link>
            </span>
          </label>
          {errors.agreeToTerms && <p className="mt-1.5 text-sm text-error">{errors.agreeToTerms.message}</p>}
        </div>
        <HumanCheck ref={check} onToken={setToken} />
        <FormError message={error} />
        <Button type="submit" size="lg" loading={isLoading} disabled={waitingForCheck} className="w-full">Create account</Button>
        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-accent-text hover:underline">Sign in</Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default RegisterPage;
